import Component from '@glimmer/component'
import { service } from '@ember/service'
import { use } from 'ember-resources'
import { keepLatest } from 'reactiveweb/keep-latest'
import { trackedFunction } from 'reactiveweb/function'
import { toRequestScope, type BaseReport, type BaseReportMetaPaging } from 'teacher-ui-client/services/reporting'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { SpellingIndividualWordsTable } from 'teacher-ui-client/components/writing-legends/tables/spelling-individual-words-table'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import type Student from 'teacher-ui-client/models/student'
import { deserialize as deserializeSort } from '@blakeelearning/data-tables/utils/sorting'
import NoReportValue from 'teacher-ui-client/components/reporting/no-report-value'
import ContentTile from 'teacher-ui-client/components/tiles/content'
import { Product } from 'teacher-ui-client/utils/products'
import { array } from '@ember/helper'

export interface SpellingIndividualWordsData extends BaseReport {
  words: [
    {
      correct_word: string
      incorrect_word: string
      total_correct: number
      total_incorrect: number
    },
  ]
  meta: BaseReportMetaPaging
}

interface Signature {
  Args: {
    uiScope: ModelFor<ReportingUiScopeRoute>['uiScope']
    period: ModelFor<ReportingUiScopeRoute>['period']
    students: Student[]
    pageNumber: number
    perPage: number
  }
}

export class SpellingIndividualWords extends Component<Signature> {
  @queryParam('reporting/ui-scope/writing-legends/spelling') sort!: string

  @service reporting!: ReportingService

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  fetchData = trackedFunction(this, () => {
    const requestScope = toRequestScope(this.args.uiScope)

    const url = this.reporting.buildGravityUrl({
      product: Product.WL,
      requestScope,
      reportName: 'spelling/words',
    })

    const query = this.reporting.buildQuery({
      sort: this.sort,
      period: this.args.period,
    })

    return this.reporting.fetchReport<SpellingIndividualWordsData>(url, query)
  })

  @use
  latestData = keepLatest({
    value: () => this.fetchData.value,
    when: () => this.fetchData.isPending,
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    {{#if this.latestData}}
      <ContentTile>
        <SpellingIndividualWordsTable
          class="w-full"
          @isReloading={{this.fetchData.isPending}}
          @spellingData={{this.latestData}}
        />
      </ContentTile>
    {{else}}
      <NoReportValue @reportStates={{array this.fetchData}} />
    {{/if}}
  </template>
}
